'use client'

import React, { FC, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Anchor, Box, Breadcrumbs, Flex, Text, Title } from '@mantine/core'

import { usePathname, useRouter } from 'next/navigation'
import Link from 'next/link'
import { useUserContext } from '@/contexts/UserContext'
import { getDisplayableName, isStoreCategoryAllPath, isStoreCategoryPath } from '@/types/enums/StoreTypes'
import { useStoreTagsInfo } from '@/components/NavBar/NavBarUtilities'
import { isAnyStorePage, isStoreHomePage } from '@/lib/namedRoutes'
import { StoreAnalytics, trackStoreEvent } from '@nile/mantine-theme/analytics'
import { useBreakpoint } from '@/hooks/useBreakpoints'
import { headerFoucVariants } from '@/lib/animations'

import SearchHeader from '@/components/Refresh/SearchHeader/SearchHeader'
import InsurancePill from '@/components/Refresh/InsuranceWidget/InsurancePill'

interface NavBarSubheaderProps {
    setShowSearchDrawer: (value: boolean) => void
    shouldShowPrimaryColors?: boolean
    shouldShowSubheader?: boolean
}

const NavBarSubheader: FC<NavBarSubheaderProps> = ({
    setShowSearchDrawer,
    shouldShowPrimaryColors,
    shouldShowSubheader,
}) => {
    const { user, fetchUser } = useUserContext()
    const router = useRouter()
    const pathname = usePathname()
    const { isSm } = useBreakpoint()
    const isMobile = isSm

    const isSearchPath = pathname.startsWith('/store/search')
    const isValidStoreCategoryPath = isStoreCategoryPath(pathname) && !isStoreHomePage(pathname)
    const isValidStoreCategoryAllPath = isStoreCategoryAllPath(pathname) && !isStoreHomePage(pathname)
    const tag = isValidStoreCategoryPath
        ? pathname.split('/').filter(Boolean).at(-1)!
        : isValidStoreCategoryAllPath
          ? pathname.split('/').filter(Boolean).at(-2)!
          : null
    const storeTagsInfo = useStoreTagsInfo(tag)

    useEffect(() => {
        if (!user) {
            fetchUser()
        }
    }, [user])

    // Redirect logic for invalid store pages
    useEffect(() => {
        if (
            isAnyStorePage(pathname) &&
            !isSearchPath &&
            !isStoreHomePage(pathname) &&
            !isValidStoreCategoryAllPath &&
            !isValidStoreCategoryPath
        ) {
            router.push('/store/home')
        }
    }, [pathname, router, isSearchPath, isValidStoreCategoryAllPath, isValidStoreCategoryPath])

    if (!shouldShowSubheader) {
        return null
    }

    return (
        <Flex pt={isMobile ? 4 : 12} w={'100%'} justify={'center'}>
            <Box w={isMobile ? '100dvw' : '50vw'}>{renderSubheaderContent()}</Box>
        </Flex>
    )

    function renderSubheaderContent() {
        if (isStoreHomePage(pathname)) {
            return renderStoreHomeSubheader()
        }

        if (isSearchPath) {
            return null
        }

        if (isValidStoreCategoryPath) {
            return renderStoreCategoryHeader()
        }

        if (isValidStoreCategoryAllPath) {
            return renderCategoryAllServicesHeader()
        }
    }

    function renderStoreHomeSubheader() {
        const handleSearchClick = () => {
            setShowSearchDrawer(true)
            trackStoreEvent(StoreAnalytics.SEARCH_START, { location: 'subheader' })
        }
        return <SearchHeader handleSearchClick={handleSearchClick} loggedIn={!!user} />
    }

    function renderCategoryAllServicesHeader() {
        return (
            <Box mx="auto">
                {renderBreadcrumbs()}
                {renderCategoryAllServicesInfo()}
            </Box>
        )
    }

    function renderCategoryAllServicesInfo() {
        if (!storeTagsInfo) return null
        return (
            <Title size={36} fw={'var(--weight-display-small, 450)'} c="#1F1E1D" lh={'120%'} lts={'-0.36px'} pb={8}>
                {isMobile && storeTagsInfo.mobileTitle ? storeTagsInfo.mobileTitle : storeTagsInfo.title}
            </Title>
        )
    }

    function renderStoreCategoryHeader() {
        return (
            <Box mb={32} w={'100%'} mx="auto">
                {/* {renderBreadcrumbs()} */}
                {renderCategoryInfo()}
            </Box>
        )
    }

    function renderCategoryInfo() {
        // Render h1 immediately with fallback from URL slug for faster LCP
        const immediateTitle = getDisplayableName(tag || '')
        const displayTitle = storeTagsInfo
            ? isMobile && storeTagsInfo.mobileTitle
                ? storeTagsInfo.mobileTitle
                : storeTagsInfo.title
            : immediateTitle

        return (
            <motion.div variants={headerFoucVariants} initial="hidden" animate="visible">
                <Flex direction="column" align="center" gap={16} py={50}>
                    <Title
                        ta="center"
                        order={1}
                        fz={54}
                        fw={'var(--weight-display-large, 400)'}
                        c={'rgb(31, 30, 29)'}
                        lts={'-0.96px'}
                        lh={'110%'}
                    >
                        {displayTitle}
                    </Title>
                    {storeTagsInfo?.subtitle && (
                        <Text
                            ta="center"
                            fz={18}
                            fw={'var(--weight-text, 500)'}
                            lh={'130%'}
                            c={'rgb(31, 30, 29)'}
                            maw={isMobile ? 345 : 800}
                        >
                            {storeTagsInfo.subtitle}
                        </Text>
                    )}
                    {!user && <InsurancePill />}
                </Flex>
            </motion.div>
        )
    }

    function renderBreadcrumbs() {
        const segments = pathname.split('/').filter(Boolean)
        const breadcrumbs = segments.map((segment, index) => ({
            title: decodeURIComponent(segment),
            href: '/' + segments.slice(0, index + 1).join('/'),
        }))
        const lastBreadcrumbIndex = breadcrumbs.length - 1

        return (
            <Breadcrumbs
                mb={16}
                styles={{
                    separator: {
                        color: shouldShowPrimaryColors ? '#E8E5E3' : '#6E6D6C',
                    },
                }}
            >
                {breadcrumbs.map((item, index) => {
                    if (index === lastBreadcrumbIndex) {
                        return (
                            <Text key={index} c={shouldShowPrimaryColors ? 'blue.4' : 'gray.5'} fw={500}>
                                {getDisplayableName(item.title)}
                            </Text>
                        )
                    } else {
                        return (
                            <Anchor
                                component={Link}
                                href={item.href}
                                onClick={() => trackStoreEvent(StoreAnalytics.CLICKED_BREADCRUMB, { name: item.title })}
                                key={index}
                                c={shouldShowPrimaryColors ? 'gray.1' : 'gray.6'}
                                fw={500}
                            >
                                {getDisplayableName(item.title)}
                            </Anchor>
                        )
                    }
                })}
            </Breadcrumbs>
        )
    }

}

export default NavBarSubheader
